// utils/exportInvoicesToExcel.js
import * as XLSX from "xlsx";

const formatDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
};

const formatCurrency = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const exportInvoicesToExcel = (invoices, fileName = "invoices") => {
  if (!invoices?.length) {
    console.warn("No invoices to export");
    return;
  }

  const allRows = invoices.map((inv) => ({
    "Invoice #": inv.invoiceNumber || "",
    "IRN /FSN": inv.irn || "",
    "WTH Receipt #": inv.whReceiptNumber || "",
    "Customer /Supplier Name": inv.partnerName || "",
    TIN: inv.partnerTin || "",
    Branch: inv.branchName || "",
    "Sales Person": (inv.salesPerson || "").trim(),
    "Invoice Date": formatDate(inv.invoiceDate),
    "Created At": formatDate(inv.createdAt),

    Subtotal: formatCurrency(
      Number(inv.subtotal || 0) + Number(inv.nonTaxableAmount || 0)
    ),
    Discount: formatCurrency(inv.discount),
    Excise: formatCurrency(
      Number(inv.nonTaxableExcise || 0) + Number(inv.excise || 0)
    ),
    Tax: formatCurrency(inv.tax),
    Withhold: formatCurrency(inv.withhold),
    VatWithhold: formatCurrency(inv.vatWithhold),
    "Total Amount": formatCurrency(inv.totalAmount),
    "Paid Amount": formatCurrency(inv.paidAmount),
    "Unpaid Amount": formatCurrency(inv.balanceAmount),

    "Tax Type": inv.taxType || "",
    Status: inv.status || "",
    "Payment Count": Number(inv.paymentCount) || 0,
  }));

  // Monetary columns that should be hidden when all values are <= 0.
  const amountColumns = [
    "Subtotal",
    "Discount",
    "Excise",
    "Tax",
    "Withhold",
    "VatWithhold",
    "Total Amount",
    "Paid Amount",
    "Unpaid Amount",
  ];

  // Keep a monetary column only if at least one invoice has value > 0.
  const visibleAmountColumns = amountColumns.filter((column) =>
    allRows.some((row) => row[column] > 0)
  );

  // Remove monetary columns that contain no positive values.
  const rows = allRows.map((row) =>
    Object.fromEntries(
      Object.entries(row).filter(
        ([key]) =>
          !amountColumns.includes(key) || visibleAmountColumns.includes(key)
      )
    )
  );

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const headers = Object.keys(rows[0]);
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // Format retained monetary columns as numeric Excel cells.
  visibleAmountColumns.forEach((column) => {
    const columnIndex = headers.indexOf(column);

    for (let row = 1; row <= range.e.r; row++) {
      const address = XLSX.utils.encode_cell({
        r: row,
        c: columnIndex,
      });

      const cell = worksheet[address];

      if (cell) {
        cell.t = "n";
        cell.z = "#,##0.00";
      }
    }
  });

  // Format payment count as an integer.
  const paymentCountIndex = headers.indexOf("Payment Count");

  if (paymentCountIndex !== -1) {
    for (let row = 1; row <= range.e.r; row++) {
      const address = XLSX.utils.encode_cell({
        r: row,
        c: paymentCountIndex,
      });

      if (worksheet[address]) {
        worksheet[address].t = "n";
        worksheet[address].z = "0";
      }
    }
  }

  // Add SUM row at the bottom of numeric columns
  const totalRow = range.e.r + 1;

  const totalLabelCell = XLSX.utils.encode_cell({
    r: totalRow,
    c: 0,
  });

  worksheet[totalLabelCell] = {
    t: "s",
    v: "TOTAL",
  };

  const numericColumns = [...visibleAmountColumns, "Payment Count"];

  numericColumns.forEach((column) => {
    const columnIndex = headers.indexOf(column);

    if (columnIndex === -1) return;

    const columnLetter = XLSX.utils.encode_col(columnIndex);
    const firstDataRow = 2; // Excel row 2: first invoice (header is row 1)
    const lastDataRow = totalRow; // Excel row before the total row

    const cellAddress = XLSX.utils.encode_cell({
      r: totalRow,
      c: columnIndex,
    });

    worksheet[cellAddress] = {
      t: "n",
      f: `SUM(${columnLetter}${firstDataRow}:${columnLetter}${lastDataRow})`,
      v: 0,
      z: column === "Payment Count" ? "0" : "#,##0.00",
    };
  });

  // Extend worksheet range to include the total row
  range.e.r = totalRow;
  worksheet["!ref"] = XLSX.utils.encode_range(range);

  // Set column widths.
  worksheet["!cols"] = headers.map((key) => ({
    wch: Math.max(key.length, 14),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");

  const timestamp = new Date().toISOString().slice(0, 10);

  XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
};
